import { FC } from "react";

import { Link } from "../../../../utils/routing";
import { pageUrl } from "../../../../utils/urls";

import { PublishStatus } from "./PublishStatus";
import { StatusCell } from "./assessment-dashboard/StatusCell";
import { SubmissionsCell } from "./assessment-dashboard/SubmissionsCell";
import {
  AllAssessmentDashboardDataType,
  AssessmentStatusTableSortField,
  useSortableRows,
} from "./hooks";

import { MarkAssessmentCompleteMenu } from "components/materials/section/assessment/helpers";
import { ScrollableTableContainer } from "components/tables/ScrollableTableContainer";
import { SortableTH, TD, THead, TR, Table } from "components/tables/TableElements";
import { t } from "i18n/i18n";
import { Tooltip } from "mds/components/Tooltip";
import { EyeNotVisibleIcon } from "mds/icons";
import { CSVData } from "utils/file";

type AssessmentStatusTableProps = {
  tableRows: AllAssessmentDashboardDataType["allAssessmentsTableRows"];
  sectionId: string;
  title: string;
  hideMarkCompleteMenu?: boolean;
  hiddenColumns?: Set<AssessmentStatusTableSortField>;
  defaultSortField?: AssessmentStatusTableSortField;
  defaultSortDirection?: "asc" | "desc";
  emptyDescription: string;
  exportData?: CSVData | (() => Promise<CSVData>);
  onRefetch?: () => void;
};

export const AssessmentStatusTable: FC<AssessmentStatusTableProps> = ({
  tableRows,
  exportData,
  sectionId,
  title,
  hideMarkCompleteMenu,
  hiddenColumns,
  defaultSortField,
  defaultSortDirection,
  emptyDescription,
  onRefetch,
}) => {
  const { sortedRows, sortField, sortDirection, handleSort } = useSortableRows(
    tableRows,
    defaultSortField,
    defaultSortDirection,
  );
  const hidden = hiddenColumns ?? new Set<AssessmentStatusTableSortField>();
  const show = (field: AssessmentStatusTableSortField) => !hidden.has(field);

  if (tableRows.length === 0) {
    return (
      <ScrollableTableContainer title={title}>
        <div className="ml-4 text-black-tint-40">{emptyDescription}</div>
      </ScrollableTableContainer>
    );
  }

  return (
    <ScrollableTableContainer exportData={exportData} title={title}>
      <Table>
        <THead>
          <TR className="border-black-tint-70">
            <SortableTH
              activeField={sortField}
              className="w-6/12 pl-4"
              field="topic"
              label={t("assessment_dashboard.all.page_topic_header_title")}
              sortDirection={sortDirection}
              onSort={handleSort}
            />
            <SortableTH
              activeField={sortField}
              className="w-6/12"
              field="page"
              label={t("assessment_dashboard.all.page_header_title")}
              sortDirection={sortDirection}
              onSort={handleSort}
            />
            {show("status") && (
              <SortableTH
                activeField={sortField}
                className="w-16"
                field="status"
                label={t("assessment_dashboard.all.status_header_title")}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
            )}
            {show("publishing") && (
              <SortableTH
                activeField={sortField}
                className="w-16"
                field="publishing"
                label={t("assessment_dashboard.all.publishing_header_title")}
                sortDirection={sortDirection}
                tooltip={t("assessment_dashboard.all.publishing_header_tooltip")}
                onSort={handleSort}
              />
            )}
            {show("submissions") && (
              <SortableTH
                activeField={sortField}
                className="w-16"
                field="submissions"
                label={t("assessment_dashboard.all.submissions_header_title")}
                sortDirection={sortDirection}
                tooltip={t("assessment_dashboard.all.submissions_header_tooltip")}
                onSort={handleSort}
              />
            )}
            {show("progress") && (
              <SortableTH
                activeField={sortField}
                className="w-16"
                field="progress"
                label={t("assessment_dashboard.all.progress_header_title")}
                sortDirection={sortDirection}
                tooltip={t("assessment_dashboard.all.progress_header_tooltip")}
                onSort={handleSort}
              />
            )}
            {show("assessment_category") && (
              <SortableTH
                activeField={sortField}
                className="w-16"
                field="assessment_category"
                label={t("assessment_dashboard.all.assessment_category_header_title")}
                sortDirection={sortDirection}
                tooltip={t("assessment_dashboard.all.assessment_category_header_tooltip")}
                onSort={handleSort}
              />
            )}
            {show("avg_points") && (
              <SortableTH
                activeField={sortField}
                className="w-16"
                field="avg_points"
                label={t("assessment_dashboard.all.avg_points_header_title")}
                sortDirection={sortDirection}
                tooltip={t("assessment_dashboard.all.avg_points_header_tooltip")}
                onSort={handleSort}
              />
            )}
            {show("outcomes") && (
              <SortableTH
                activeField={sortField}
                className="w-16"
                field="outcomes"
                label={t("assessment_dashboard.all.outcomes_header_title")}
                sortDirection={sortDirection}
                tooltip={t("assessment_dashboard.all.outcomes_header_tooltip")}
                onSort={handleSort}
              />
            )}
            {show("last_submitted") && (
              <SortableTH
                activeField={sortField}
                className="w-16"
                field="last_submitted"
                label={t("assessment_dashboard.all.last_submitted_header_title")}
                sortDirection={sortDirection}
                tooltip={t("assessment_dashboard.all.last_submitted_header_tooltip")}
                onSort={handleSort}
              />
            )}
            {show("last_assessed") && (
              <SortableTH
                activeField={sortField}
                className="w-16"
                field="last_assessed"
                label={t("assessment_dashboard.all.last_assessed_header_title")}
                sortDirection={sortDirection}
                tooltip={t("assessment_dashboard.all.last_assessed_header_tooltip")}
                onSort={handleSort}
              />
            )}
            {show("due_date") && (
              <SortableTH
                activeField={sortField}
                className="w-16"
                field="due_date"
                label={t("assessment_dashboard.all.due_date_header_title")}
                sortDirection={sortDirection}
                tooltip={t("assessment_dashboard.all.due_date_header_tooltip")}
                onSort={handleSort}
              />
            )}
          </TR>
        </THead>

        <tbody>
          {sortedRows.map((row) => (
            <TR key={row.id}>
              <TD className="max-w-52 truncate py-2.5 pl-[17px]" title={row.topicTitle || "–"}>
                {row.topicTitle || "–"}
              </TD>

              <TD className="max-w-40 truncate" title={row.title}>
                <Link
                  className="body-xs flex min-w-0 items-center gap-1 truncate text-blue-shade-20 underline"
                  to={pageUrl(sectionId, row.id)}
                >
                  {row.hidden && (
                    <Tooltip text={t("assessment_dashboard.all.hidden_page_tooltip")}>
                      <EyeNotVisibleIcon className="shrink-0 text-black-tint-40" />
                    </Tooltip>
                  )}
                  <span className="truncate">{row.title}</span>
                </Link>
              </TD>

              {show("status") && (
                <TD className="max-w-40 truncate">
                  <StatusCell row={row} sectionId={sectionId} />
                </TD>
              )}

              {show("publishing") && (
                <TD>
                  <PublishStatus status={row.publishStatus} />
                </TD>
              )}

              {show("submissions") && (
                <TD>
                  <SubmissionsCell row={row} />
                </TD>
              )}

              {show("progress") && <TD>{row.progress}</TD>}

              {show("assessment_category") && <TD>{row.assessmentCategory}</TD>}

              {show("avg_points") && <TD>{row.avgPoints}</TD>}

              {show("outcomes") && (
                <TD>
                  <Tooltip text={row.outcomesTooltip}>{row.outcomesCount.toString()}</Tooltip>
                </TD>
              )}

              {show("last_submitted") && <TD>{row.lastSubmittedDate}</TD>}

              {show("last_assessed") && <TD>{row.lastAssessedDate}</TD>}

              {show("due_date") && <TD>{row.dueAtDate}</TD>}

              {!hideMarkCompleteMenu && (
                <td>
                  <MarkAssessmentCompleteMenu
                    isMarkedComplete={row.isMarkedComplete}
                    pageId={row.id}
                    pageTitle={row.title}
                    sectionId={sectionId}
                    isCourseAssessment
                    onChange={onRefetch ?? (() => {})}
                  />
                </td>
              )}
            </TR>
          ))}
        </tbody>
      </Table>
    </ScrollableTableContainer>
  );
};
